// Copyright © 2021 Move Closer
import { __decorate } from "tslib";
import { Component, Prop, Provide, Vue } from 'vue-property-decorator';
import { AppTheme, ImageRatio, ModuleBackgroundColor, moduleClassesMap } from '../_contracts';
import { LineHeaderTheme } from '../_components/LineHeader/LineHeader.contracts';
import { isLight } from '../../support/color';
/**
 * The abstract class that every module's UI **HAS TO** extend.
 *
 * @author Olga Milczek <olga.milczek@movecloser.pl>
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl>
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
let AbstractModuleUi = class AbstractModuleUi extends Vue {
    constructor() {
        super(...arguments);
        this.providedEventBus = this.eventBus;
        /**
         * This notation provides the `theme` @Prop down to every child of the component.
         */
        this.providedTheme = this.theme;
        /**
         * Determines whether the module is still in the loading phase.
         */
        this.isLoading = true;
        /**
         * Determines what image ratio should be used in particular module.
         */
        this.imageRatios = {
            desktop: ImageRatio.Original,
            mobile: ImageRatio.Original
        };
    }
    /**
     * Value for the `background` CSS declaration.
     *
     * @see CanHaveBackground
     */
    get background() {
        return typeof this.module.content.background === 'string' && this.module.content.background.length > 0
            ? this.module.content.background
            : undefined;
    }
    get getThemeByBgClass() {
        return '--' + this.getThemeByBg;
    }
    /**
     * @inheritDoc
     */
    get getThemeByBg() {
        switch (true) {
            case this.module.content.backgroundColor === ModuleBackgroundColor.None:
                return LineHeaderTheme.Light;
            case this.module.content.background !== undefined && this.module.content.background !== null && this.module.content.background !== '':
                return isLight(this.module.content.background) ? LineHeaderTheme.Light : LineHeaderTheme.Dark;
            case this.module.content.backgroundColor === ModuleBackgroundColor.Blue:
            case this.module.content.backgroundColor === ModuleBackgroundColor.Brown:
            case this.module.content.backgroundColor === ModuleBackgroundColor.DarkGreen:
            case this.module.content.backgroundColor === ModuleBackgroundColor.DarkGray:
                return LineHeaderTheme.Dark;
            default:
                return LineHeaderTheme.Light;
        }
    }
    /**
     * The CSS class defining whether the module is in the `debug` mode.
     */
    get debugClass() {
        return this.debug ? '--debug' : '';
    }
    /**
     * Determines whether the current environment is _Storybook.js_.
     */
    get isStorybook() {
        if (!process.env) {
            return false;
        }
        if (!Object.prototype.hasOwnProperty.call(process.env, 'STORYBOOK')) {
            return false;
        }
        if (typeof process.env.STORYBOOK !== 'string') {
            return false;
        }
        return process.env.STORYBOOK === 'true';
    }
    /**
     * Determines image ratio which should be used.
     */
    get imageRatio() {
        if (this.imageRatios === null) {
            throw new Error('AbstractModuleUi.imageRatio(): [this.imageRatios] is [null], but its value should be set from/inside the subclass!');
        }
        if (this.isMobile) {
            return this.imageRatios.mobile;
        }
        return this.imageRatios.desktop;
    }
    /**
     * Render with appropriate color
     */
    get customColor() {
        return (this.module.content.backgroundColor === ModuleBackgroundColor.Custom &&
            this.module.content.background &&
            this.module.content.background !== '') ? this.module.content.background : 'inherit';
    }
    /**
     * Render with appropriate color
     */
    get moduleColoredClass() {
        return (this.module.size.colSpan === 6 && this.module.content.backgroundColor !== ModuleBackgroundColor.None)
            ? moduleClassesMap[this.module.content.backgroundColor]
            : moduleClassesMap[ModuleBackgroundColor.None];
    }
    /**
     * The CSS class responsible for the module's loading indicator.
     */
    get loadingClass() {
        return '';
        // return this.isLoading ? '--loading' : ''
    }
    /**
     * The CSS class defining the module's size.
     */
    get sizeClass() {
        const { colSpan, rowSpan } = this.module.size;
        return `--size-${colSpan}-${rowSpan}`;
    }
    /**
     * The CSS class defining the current application theme.
     */
    get themeClass() {
        return `--theme-${this.theme.replace(/_/g, '-')}`;
    }
    /**
     * The CSS class defining the module's version.
     */
    get versionClass() {
        if (typeof this.module.version !== 'string') {
            return '';
        }
        return `--version-${this.module.version.toLowerCase()}`;
    }
    /**
     * Handles the module's errors.
     */
    onError(error) {
        this.markAsLoading();
        if (error && this.debug)
            console.warn(error);
    }
    /**
     * Handles the module's ready state.
     */
    onReady() {
        this.markAsReady();
    }
    /**
     * Marks the module as loading (reveals the skeleton screen).
     */
    markAsLoading() {
        this.isLoading = true;
    }
    /**
     * Marks the module as ready (hides the skeleton screen).
     */
    markAsReady() {
        this.isLoading = false;
    }
};
__decorate([
    Prop({
        type: Boolean,
        required: false,
        default: false
    })
], AbstractModuleUi.prototype, "debug", void 0);
__decorate([
    Prop({
        type: Object,
        required: false
    })
], AbstractModuleUi.prototype, "DeMarked", void 0);
__decorate([
    Prop({
        type: Object,
        required: true
    })
], AbstractModuleUi.prototype, "eventBus", void 0);
__decorate([
    Prop({
        type: String,
        required: false,
        default: ''
    })
], AbstractModuleUi.prototype, "fallbackImage", void 0);
__decorate([
    Prop({
        type: Boolean,
        required: false,
        default: true
    })
], AbstractModuleUi.prototype, "isMobile", void 0);
__decorate([
    Prop({
        type: Object,
        required: true
    })
], AbstractModuleUi.prototype, "module", void 0);
__decorate([
    Prop({
        type: Object,
        required: false
    })
], AbstractModuleUi.prototype, "parent", void 0);
__decorate([
    Prop({
        type: Object,
        required: false,
        default: null
    })
], AbstractModuleUi.prototype, "pdfService", void 0);
__decorate([
    Prop({
        type: Object,
        required: true
    })
], AbstractModuleUi.prototype, "relatedService", void 0);
__decorate([
    Prop({
        type: Object,
        required: false
    })
], AbstractModuleUi.prototype, "siteService", void 0);
__decorate([
    Prop({
        type: Object,
        required: false
    })
], AbstractModuleUi.prototype, "commentService", void 0);
__decorate([
    Prop({
        type: Object,
        required: false
    })
], AbstractModuleUi.prototype, "reactionsService", void 0);
__decorate([
    Prop({
        type: Object,
        required: false
    })
], AbstractModuleUi.prototype, "shareService", void 0);
__decorate([
    Prop({
        type: Object,
        required: false
    })
], AbstractModuleUi.prototype, "subscriptionService", void 0);
__decorate([
    Prop({
        type: String,
        required: false,
        default: AppTheme.Default
    })
], AbstractModuleUi.prototype, "theme", void 0);
__decorate([
    Prop({
        type: Object,
        required: true
    })
], AbstractModuleUi.prototype, "windowService", void 0);
__decorate([
    Provide('eventBus')
], AbstractModuleUi.prototype, "providedEventBus", void 0);
__decorate([
    Provide('theme')
], AbstractModuleUi.prototype, "providedTheme", void 0);
AbstractModuleUi = __decorate([
    Component({
        name: 'AbstractModuleUi',
        beforeMount() {
            // @ts-expect-error
            if (this.isStorybook && typeof this.fetchRelated === 'function') {
                // @ts-expect-error
                this.fetchRelated();
            }
        }
    })
], AbstractModuleUi);
export { AbstractModuleUi };
